import { useState, useEffect } from 'react';
import { Mail, AlertCircle, CheckCircle, Clock, RefreshCw, Filter, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, recent_failed: 0, resend_configured: false });
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [statusFilter, typeFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('email_type', typeFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/admin/email-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Errore nel caricamento logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/email-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const retryEmail = async (logId) => {
    setRetrying(logId);
    try {
      const response = await api.post(`/admin/email-logs/${logId}/retry`);
      if (response.data.success) {
        toast.success('Email reinviata');
      } else {
        toast.error('Invio fallito');
      }
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Errore nel reinvio');
    } finally {
      setRetrying(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'failed':
        return <AlertCircle size={16} className="text-red-600" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-600" />;
      default:
        return <Mail size={16} className="text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      pending: 'bg-yellow-100 text-yellow-700',
    };
    const labels = {
      sent: 'Inviata',
      failed: 'Fallita',
      pending: 'In attesa',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${variants[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      'nuova_prenotazione_admin': 'Nuova Prenotazione (Admin)',
      'nuova_prenotazione_cliente': 'Nuova Prenotazione (Cliente)',
      'prenotazione_accettata': 'Prenotazione Accettata',
      'pagamento_caparra': 'Pagamento Caparra',
      'pagamento_caparra_admin': 'Pagamento Caparra (Admin)',
      'cancellazione_con_rimborso': 'Cancellazione + Rimborso',
      'cancellazione_senza_rimborso': 'Cancellazione',
      'newsletter': 'Newsletter',
      'notification': 'Notifica',
      'generic': 'Generico',
      'retry': 'Reinvio',
    };
    return labels[type] || type;
  };

  const emailTypes = [
    'nuova_prenotazione_admin',
    'nuova_prenotazione_cliente',
    'prenotazione_accettata',
    'pagamento_caparra',
    'pagamento_caparra_admin',
    'cancellazione_con_rimborso',
    'cancellazione_senza_rimborso',
    'newsletter',
    'notification',
  ];

  return (
    <div data-testid="admin-email-logs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Email Logs</h1>
          <p className="text-muted-foreground">Monitoraggio invio email</p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
          <RefreshCw size={16} className="mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Totali</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Inviate</p>
            <p className="text-2xl font-bold text-green-700">{stats.sent}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Fallite</p>
            <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card className={`${stats.recent_failed > 0 ? 'border-orange-200 bg-orange-50/50' : ''}`}>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Fallite (24h)</p>
            <p className={`text-2xl font-bold ${stats.recent_failed > 0 ? 'text-orange-700' : ''}`}>
              {stats.recent_failed}
            </p>
          </CardContent>
        </Card>
        <Card className={`${stats.resend_configured ? 'border-green-200' : 'border-red-200 bg-red-50/50'}`}>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Resend API</p>
            <p className={`text-lg font-bold ${stats.resend_configured ? 'text-green-700' : 'text-red-700'}`}>
              {stats.resend_configured ? '✓ Configurato' : '✗ Non configurato'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Warning if Resend not configured */}
      {!stats.resend_configured && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded flex items-start gap-3">
          <AlertCircle className="text-red-600 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-red-800">Resend non configurato</p>
            <p className="text-sm text-red-600">
              Le email non vengono inviate. Configura RESEND_API_KEY nel file .env del backend.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="py-4">
          <div className="flex items-center gap-2">
            <Filter size={18} />
            <CardTitle className="text-base">Filtri</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="sent">Inviate</SelectItem>
                <SelectItem value="failed">Fallite</SelectItem>
                <SelectItem value="pending">In attesa</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Tipo email" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {emailTypes.map(type => (
                  <SelectItem key={type} value={type}>{getTypeLabel(type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Stato</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Destinatario</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Oggetto</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Data</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30" data-testid={`email-log-${log.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{getTypeLabel(log.email_type)}</td>
                    <td className="px-4 py-3 text-sm font-mono">{log.to}</td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate">{log.subject}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(log.created_at).toLocaleString('it-IT')}
                    </td>
                    <td className="px-4 py-3">
                      {log.status === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => retryEmail(log.id)}
                          disabled={retrying === log.id}
                        >
                          {retrying === log.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {logs.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              Nessun log email trovato
            </div>
          )}
        </Card>
      )}

      {/* Error Details (for failed emails) */}
      {logs.some(l => l.status === 'failed' && l.error) && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base text-red-700">Errori Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {logs.filter(l => l.status === 'failed' && l.error).slice(0, 5).map(log => (
                <div key={log.id} className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                  <p className="font-medium text-red-800">{log.subject}</p>
                  <p className="text-red-600 text-xs mt-1">To: {log.to}</p>
                  <p className="text-red-700 mt-2 font-mono text-xs">{log.error}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
